/**
 * useBottomPanelAutoOpen — Shared panel auto-open behavior for designer nodes
 *
 * Implements two useEffect hooks:
 * 1. Selection effect: open on select (reset to 'config'), close on deselect
 * 2. Regeneration effect: open to 'variants' when running + batchPending > 0
 *
 * Sprint 3a: Designer Nodes — Shared Foundation
 */

import { useEffect, useRef } from 'react'
import { useStore } from 'reactflow'
import { getSelectedNodeCount, shouldShowSingleNodeDetails } from './selectionDetails'

interface UseBottomPanelAutoOpenOptions {
  selected: boolean | undefined
  isRunning: boolean
  batchPending: number
  setBottomPanelOpen: React.Dispatch<React.SetStateAction<boolean>>
  setActiveTab: (tab: string) => void
  defaultTab?: string
}

/**
 * Wire up the standard bottom panel auto-open lifecycle for designer nodes.
 *
 * - When `selected` becomes true and not running: open panel, reset tab to 'config' if it was closed
 * - When `selected` becomes false: close panel
 * - When `status === 'running'` and `batchPending > 0`: open panel to 'variants'
 */
export function useBottomPanelAutoOpen({
  selected,
  isRunning,
  batchPending,
  setBottomPanelOpen,
  setActiveTab,
  defaultTab,
}: UseBottomPanelAutoOpenOptions): void {
  const hasAutoSwitchedRef = useRef(false)
  const prevRunningRef = useRef(false)
  const selectedNodeCount = useStore(getSelectedNodeCount)
  const canShowDetails = shouldShowSingleNodeDetails(selectedNodeCount)

  // Auto-open panel on selection
  useEffect(() => {
    if (selected && canShowDetails) {
      setBottomPanelOpen((wasOpen) => {
        if (!wasOpen) setActiveTab(defaultTab ?? 'config')
        return true
      })
    } else {
      setBottomPanelOpen(false)
    }
  }, [selected, canShowDetails, setBottomPanelOpen, setActiveTab, defaultTab])

  // Auto-open variants tab on regeneration
  useEffect(() => {
    if (isRunning && batchPending > 0 && canShowDetails && !hasAutoSwitchedRef.current) {
      setActiveTab('variants')
      setBottomPanelOpen(true)
      hasAutoSwitchedRef.current = true
    }
  }, [isRunning, batchPending, canShowDetails, setBottomPanelOpen, setActiveTab])

  // Reset auto-switch ref when generation ends
  useEffect(() => {
    if (prevRunningRef.current && !isRunning) {
      hasAutoSwitchedRef.current = false
    }
    prevRunningRef.current = isRunning
  }, [isRunning])
}
